import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';
import { detectPlatform } from './session-storage';

/**
 * LocalNotificationsPort — the device's local notification system, behind the
 * ports layer (ARCHITECTURE §3, INV-T20: `src/platform/` is the ONLY place
 * `@capacitor/*` may be imported). WD-3.
 *
 * **THE DEVICE SCHEDULE IS DERIVED AND DISPOSABLE** (PT §14): the database is
 * the authority on what is due, and this port only makes the phone's pending
 * notifications converge on that truth. Nothing here writes product state,
 * and nothing a notification carries can become write authority — a tap
 * navigates, and that is all it does.
 *
 * **CONFIRMED NATIVE ONLY.** The same three-valued platform posture the
 * session store established (R1 2026-08-11): `web` and `unknown` both get the
 * unsupported port, which never pretends a notification was scheduled —
 * `isSupported()` is false, permission is `'unsupported'`, and every
 * operation is an honest no-op. My Day itself works identically either way.
 *
 * **EXPLICITLY INEXACT, ALWAYS (review B2).** Every schedule call sends the
 * plugin's real `isExactNotification: false` — the field DEFAULTS TO TRUE,
 * and the default path opens Android's "Alarms & reminders" settings screen
 * to ask for the exact-alarm permission, which Crewlink must never do. There
 * is no Crewlink code path that requests an exact alarm, and the exact-alarm
 * permission itself is stripped from the merged Android manifest.
 *
 * **SCHEDULED-ONLY DEVICE READS (review M1).** Reconciliation reads
 * `getAll({ state: 'SCHEDULED' })` — pending schedule truth only. Delivered
 * (TRIGGERED) notifications are shade history, not schedule, and treating
 * them as pending was how a delivered alert could look "still satisfied".
 *
 * **REBOOT (corrected posture):** the plugin's own boot-restore receiver and
 * its RECEIVE_BOOT_COMPLETED permission are REMOVED from Crewlink's merged
 * manifest — no notification restoration ships at all. After a reboot the
 * schedule is rebuilt on the next Crewlink open/foreground reconciliation,
 * which also prevents stale pre-reboot alerts firing before Crewlink has
 * reconciled with the server. (WAKE_LOCK, declared by the plugin, is
 * retained: inspection found no direct plugin usage, but it is not proven
 * unnecessary for the OS delivery path — an implementation permission of the
 * native mechanism, never product authority.)
 */

export type NotificationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** What the OS reports as pending, with Crewlink's own metadata extracted. */
export interface PendingDeviceNotification {
  readonly id: number;
  /** From the notification's `extra` — null when it is not Crewlink's. */
  readonly itemId: string | null;
  readonly at: string | null;
  /** The signed-in user it was scheduled for (M2) — null on legacy alerts. */
  readonly ownerId: string | null;
}

export interface ScheduleDeviceNotification {
  readonly id: number;
  readonly itemId: string;
  /** Non-visible ownership metadata (M2). Never in title/body. */
  readonly ownerId: string;
  readonly at: string;
  readonly title: string;
  readonly body: string;
}

export interface LocalNotificationsPort {
  /** Human-readable implementation name. Diagnostics only, never product UI. */
  readonly name: string;
  /** True ONLY on a confirmed native runtime. Web and unknown are false. */
  isSupported(): boolean;
  /** Current permission, without prompting. Never throws. */
  checkPermission(): Promise<NotificationPermissionState>;
  /** Ask the OS. Callers decide WHEN — this port never prompts on its own. */
  requestPermission(): Promise<NotificationPermissionState>;
  /**
   * The notifications the OS still has SCHEDULED — pending schedule truth
   * only (M1). Delivered/TRIGGERED notifications are deliberately excluded:
   * they are shade history, not schedule.
   */
  getScheduled(): Promise<readonly PendingDeviceNotification[]>;
  /** Schedule these. Inexact by design; `extra` carries the local mapping. */
  schedule(items: readonly ScheduleDeviceNotification[]): Promise<void>;
  cancel(ids: readonly number[]): Promise<void>;
  /**
   * A tap on one of Crewlink's notifications. The handler NAVIGATES and does
   * nothing else — a notification is never a mutation. Returns an unsubscribe.
   */
  onTap(handler: () => void): () => void;
  /**
   * The app returning to the foreground — the reconciliation moment. Returns
   * an unsubscribe. No-op off native.
   */
  onAppActive(handler: () => void): () => void;
}

/** The plugin reports a string; only the known values are trusted. */
type PermissionValue = string;

function toPermissionState(value: PermissionValue | undefined): NotificationPermissionState {
  if (value === 'granted') return 'granted';
  if (value === 'denied') return 'denied';
  if (value === 'prompt' || value === 'prompt-with-rationale') return 'prompt';
  return 'unsupported';
}

/**
 * The slice of the REAL plugin API this port relies on — injectable for
 * tests, which therefore have to model the actual `getAll({ state })` and
 * `isExactNotification` contract rather than a convenient invention.
 */
export interface LocalNotificationsPluginLike {
  checkPermissions(): Promise<{ display: PermissionValue }>;
  requestPermissions(): Promise<{ display: PermissionValue }>;
  /** The plugin's real state-filtered read (8.3.0+). */
  getAll(options?: { state?: 'SCHEDULED' | 'TRIGGERED' }): Promise<{
    notifications: readonly { id: number; extra?: unknown }[];
  }>;
  schedule(options: {
    notifications: {
      id: number;
      title: string;
      body: string;
      /**
       * **`allowWhileIdle` IS NOT AN EXACT-ALARM REQUEST (Slice A).** In
       * `@capacitor/local-notifications` 8.3.1, a one-shot notification with
       * `allowWhileIdle: true` and `isExactNotification: false` is delivered
       * through `AlarmManager.setAndAllowWhileIdle(RTC_WAKEUP, …)`, which
       * needs NO `SCHEDULE_EXACT_ALARM` permission and never opens the
       * "Alarms & reminders" screen. It buys one thing only: the alarm is
       * allowed to fire while the device is idle in Doze, instead of being
       * deferred to the next maintenance window — which is exactly the
       * situation a 30-minute heads-up has to survive.
       */
      schedule: { at: Date; allowWhileIdle: boolean };
      /**
       * **ALWAYS SENT, ALWAYS FALSE (B2).** The plugin defaults this to
       * TRUE, and the true path can open the exact-alarm settings screen.
       */
      isExactNotification: boolean;
      extra: { crewlink: 'my_day'; itemId: string; ownerId: string; at: string };
    }[];
  }): Promise<unknown>;
  cancel(options: { notifications: { id: number }[] }): Promise<void>;
  addListener(
    event: 'localNotificationActionPerformed',
    handler: (action: { actionId: string }) => void,
  ): Promise<{ remove: () => Promise<void> }> | { remove: () => Promise<void> };
}

export interface AppPluginLike {
  addListener(
    event: 'appStateChange',
    handler: (state: { isActive: boolean }) => void,
  ): Promise<{ remove: () => Promise<void> }> | { remove: () => Promise<void> };
}

/**
 * Crewlink's own metadata, read defensively off an untyped `extra`.
 * Malformed or foreign metadata fails SAFE: not Crewlink's, so untouched —
 * and never a routing primitive or any kind of authority.
 */
function readExtra(extra: unknown): {
  itemId: string | null;
  at: string | null;
  ownerId: string | null;
} {
  if (typeof extra !== 'object' || extra === null) {
    return { itemId: null, at: null, ownerId: null };
  }
  const record = extra as {
    crewlink?: unknown;
    itemId?: unknown;
    at?: unknown;
    ownerId?: unknown;
  };
  // **ONLY NOTIFICATIONS CREWLINK MARKED ARE CREWLINK'S TO MANAGE.**
  if (record.crewlink !== 'my_day') return { itemId: null, at: null, ownerId: null };
  return {
    itemId: typeof record.itemId === 'string' ? record.itemId : null,
    at: typeof record.at === 'string' ? record.at : null,
    // Null on a legacy pre-ownership alert — reconciliation removes those.
    ownerId: typeof record.ownerId === 'string' ? record.ownerId : null,
  };
}

export function createCapacitorLocalNotifications(
  plugin: LocalNotificationsPluginLike,
  app: AppPluginLike,
): LocalNotificationsPort {
  return {
    name: 'capacitor:LocalNotifications',

    isSupported() {
      return true;
    },

    async checkPermission(): Promise<NotificationPermissionState> {
      try {
        return toPermissionState((await plugin.checkPermissions()).display);
      } catch {
        return 'unsupported';
      }
    },

    async requestPermission(): Promise<NotificationPermissionState> {
      try {
        return toPermissionState((await plugin.requestPermissions()).display);
      } catch {
        return 'unsupported';
      }
    },

    async getScheduled(): Promise<readonly PendingDeviceNotification[]> {
      try {
        // **SCHEDULED ONLY (M1)** — delivered/TRIGGERED rows are shade
        // history, not schedule truth, and must never look "still pending".
        const { notifications } = await plugin.getAll({ state: 'SCHEDULED' });
        return notifications.map((entry) => ({ id: entry.id, ...readExtra(entry.extra) }));
      } catch {
        // An unreadable scheduled list must not become "cancel everything"
        // or "schedule duplicates" — the reconciler treats it as "do
        // nothing this run".
        throw new Error('scheduled list unavailable');
      }
    },

    async schedule(items): Promise<void> {
      if (items.length === 0) return;
      await plugin.schedule({
        notifications: items.map((item) => ({
          id: item.id,
          title: item.title,
          body: item.body,
          // **THE ABSOLUTE INSTANT, AS AN INSTANT.** `Date` carries epoch
          // milliseconds; no wall-time arithmetic happens on the device, so
          // the device timezone cannot move the schedule.
          // **DOZE-SURVIVABLE, STILL INEXACT (Slice A).** `allowWhileIdle`
          // lets the alarm fire while the phone is idle; it does NOT request
          // an exact alarm and needs no extra permission. Paired with the
          // explicitly-false flag below, the plugin uses
          // `setAndAllowWhileIdle(RTC_WAKEUP, …)`.
          schedule: { at: new Date(item.at), allowWhileIdle: true },
          // **EXPLICITLY INEXACT (B2).** The plugin DEFAULTS this to true,
          // and the true path can open the exact-alarm settings screen —
          // omission is not an option.
          isExactNotification: false,
          extra: {
            crewlink: 'my_day',
            itemId: item.itemId,
            ownerId: item.ownerId,
            at: item.at,
          },
        })),
      });
    },

    async cancel(ids): Promise<void> {
      if (ids.length === 0) return;
      await plugin.cancel({ notifications: ids.map((id) => ({ id })) });
    },

    onTap(handler): () => void {
      let removed = false;
      let remove: (() => Promise<void>) | null = null;
      void Promise.resolve(
        plugin.addListener('localNotificationActionPerformed', (action) => {
          // 'tap' is the plugin's body-tap actionId. There are no action
          // buttons in WD-3, so anything else is ignored.
          if (action.actionId === 'tap') handler();
        }),
      ).then((subscription) => {
        if (removed) void subscription.remove();
        else remove = () => subscription.remove();
      });
      return () => {
        removed = true;
        if (remove) void remove();
      };
    },

    onAppActive(handler): () => void {
      let removed = false;
      let remove: (() => Promise<void>) | null = null;
      void Promise.resolve(
        app.addListener('appStateChange', (state) => {
          if (state.isActive) handler();
        }),
      ).then((subscription) => {
        if (removed) void subscription.remove();
        else remove = () => subscription.remove();
      });
      return () => {
        removed = true;
        if (remove) void remove();
      };
    },
  };
}

/**
 * The web / unknown-platform port: honest incapability. It never schedules,
 * never prompts, never pretends — and My Day works identically without it.
 */
export function createUnsupportedLocalNotifications(
  name = 'web:no-local-notifications',
): LocalNotificationsPort {
  return {
    name,
    isSupported: () => false,
    checkPermission: () => Promise.resolve('unsupported' as const),
    requestPermission: () => Promise.resolve('unsupported' as const),
    getScheduled: () => Promise.resolve([]),
    schedule: () => Promise.resolve(),
    cancel: () => Promise.resolve(),
    onTap: () => () => {},
    onAppActive: () => () => {},
  };
}

/**
 * Chosen once at startup, exactly the session store's rule: **only a
 * CONFIRMED native runtime gets the real adapter.** Web and unknown both get
 * the unsupported port — an undetermined platform must never behave as
 * confirmed native.
 */
export function selectLocalNotifications(): LocalNotificationsPort {
  return detectPlatform() === 'native'
    ? createCapacitorLocalNotifications(LocalNotifications, App)
    : createUnsupportedLocalNotifications(
        detectPlatform() === 'unknown' ? 'unknown:no-local-notifications' : undefined,
      );
}
